import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { useCurrency } from '@/context/CurrencyContext';
import { Card } from '@/components/ui/card';
import { Award, ShoppingBag, Sparkles, Crown, Check, ShieldCheck, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';
import { openInvoice } from '@/lib/invoice';

const COUNTRIES = [
  'Saudi Arabia', 'United Arab Emirates', 'Qatar', 'Bahrain', 'Kuwait', 'Oman',
  'Egypt', 'Jordan', 'Lebanon', 'Iraq', 'Morocco', 'Tunisia', 'Algeria',
  'United Kingdom', 'United States', 'Canada', 'France', 'Germany',
].sort((a, b) => a.localeCompare(b));

// Modal shown when a customer taps "Start Silver / Gold" — collects the same
// address the checkout form asks for, POSTs to /premium/subscribe, then opens
// the shared invoice so payment happens exactly like a regular order.
const SubscribeDialog = ({ tier, open, onClose, onSubscribed }) => {
  const { t } = useLanguage();
  const { format } = useCurrency();
  const [months, setMonths] = useState(1);
  const [loading, setLoading] = useState(false);
  const [address, setAddress] = useState({
    name: '', line1: '', city: '', state: '', zip: '', country: 'Saudi Arabia', phone: '',
  });

  if (!tier) return null;
  const totalUsd = (tier.price_monthly || 0) * months;

  const submit = async (e) => {
    e.preventDefault();
    const required = ['name', 'line1', 'city', 'zip', 'country', 'phone'];
    for (const k of required) {
      if (!address[k] || !String(address[k]).trim()) {
        toast.error(t('subscribe.pleaseFill', 'Please fill in {field}').replace('{field}', k));
        return;
      }
    }
    setLoading(true);
    try {
      const { data } = await api.post('/premium/subscribe', {
        tier_id: tier.id, months, address, payment_method: 'cash_on_delivery',
      });
      toast.success(t('subscribe.placedToast', 'Subscription request placed — opening your invoice.'));
      await openInvoice(data.id);
      onSubscribed?.(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('subscribe.failed', 'Could not place subscription'));
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="subscribe-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-yellow-400" />
            {t('subscribe.title', 'Start Jwalek Premium — {tier}').replace('{tier}', tier.name)}
          </DialogTitle>
          <DialogDescription>
            {t('subscribe.subtitle', "We'll generate an invoice for you to pay. Once payment is received, your premium perks activate automatically.")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          {/* Billing horizon */}
          <div>
            <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>
              {t('subscribe.billing', 'Billing')}
            </label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <button
                type="button"
                data-testid="subscribe-months-1-button"
                onClick={() => setMonths(1)}
                className={`p-3 rounded-lg border text-sm ${months === 1 ? 'jw-panel border-orange-400/60' : ''}`}
                style={{ borderColor: months === 1 ? undefined : 'var(--jw-border)' }}
              >
                <div className="font-semibold">{t('subscribe.monthly', 'Monthly')}</div>
                <div className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{format(tier.price_monthly)} / {t('subscribe.month', 'month')}</div>
              </button>
              <button
                type="button"
                data-testid="subscribe-months-12-button"
                onClick={() => setMonths(12)}
                className={`p-3 rounded-lg border text-sm text-left ${months === 12 ? 'jw-panel border-orange-400/60' : ''}`}
                style={{ borderColor: months === 12 ? undefined : 'var(--jw-border)' }}
              >
                <div className="font-semibold flex items-center gap-2">
                  {t('subscribe.annual', 'Annual')}
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 font-semibold">-2 mo</span>
                </div>
                <div className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{format(tier.price_monthly * 12)} / {t('subscribe.year', 'year')}</div>
              </button>
            </div>
          </div>

          {/* Address */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{t('checkout.fullName', 'Full name')}</label>
              <Input data-testid="subscribe-name-input" value={address.name} onChange={(e) => setAddress({ ...address, name: e.target.value })} className="mt-1" />
            </div>
            <div className="col-span-2">
              <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{t('checkout.addressLine', 'Address line')}</label>
              <Input data-testid="subscribe-line1-input" value={address.line1} onChange={(e) => setAddress({ ...address, line1: e.target.value })} className="mt-1" />
            </div>
            <div>
              <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{t('checkout.city', 'City')}</label>
              <Input data-testid="subscribe-city-input" value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} className="mt-1" />
            </div>
            <div>
              <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{t('checkout.zip', 'Postal code')}</label>
              <Input data-testid="subscribe-zip-input" value={address.zip} onChange={(e) => setAddress({ ...address, zip: e.target.value })} className="mt-1" />
            </div>
            <div>
              <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{t('checkout.phone', 'Phone')}</label>
              <Input data-testid="subscribe-phone-input" value={address.phone} onChange={(e) => setAddress({ ...address, phone: e.target.value })} className="mt-1" />
            </div>
            <div>
              <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{t('checkout.country', 'Country')}</label>
              <Select value={address.country} onValueChange={(v) => setAddress({ ...address, country: v })}>
                <SelectTrigger data-testid="subscribe-country-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-lg p-3 flex items-start gap-2 text-xs" style={{ background: 'var(--jw-bg-elev)', color: 'var(--jw-text-muted)' }}>
            <ShieldCheck className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" />
            <span>{t('subscribe.paymentNote', "You won't be charged online. We'll send the invoice, confirm the payment, and activate your tier once it clears.")}</span>
          </div>

          <DialogFooter className="!justify-between flex-row items-center gap-3">
            <div className="text-sm">
              <span style={{ color: 'var(--jw-text-muted)' }}>{t('subscribe.total', 'Total')}: </span>
              <span className="font-bold" data-testid="subscribe-total">{format(totalUsd)}</span>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={onClose} disabled={loading} data-testid="subscribe-cancel-button">
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button type="submit" disabled={loading} data-testid="subscribe-submit-button" className="jw-btn-glossy">
                {loading ? t('subscribe.placing', 'Placing...') : t('subscribe.confirm', 'Get invoice')}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Profile = () => {
  const { user, refreshUser } = useAuth();
  const { t } = useLanguage();
  const { format } = useCurrency();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [rules, setRules] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);

  const loadOrders = () => api.get('/orders').then(({ data }) => setOrders(data)).catch(() => {});

  useEffect(() => {
    loadOrders();
    api.get('/user-designs').then(({ data }) => setDesigns(data)).catch(() => {});
    api.get('/premium-tiers').then(({ data }) => setTiers(data)).catch(() => {});
    api.get('/loyalty-rules').then(({ data }) => setRules(data)).catch(() => {});
  }, []);

  if (!user) return null;

  // A subscription order is any order our backend flagged with subtype
  // "premium_subscription". We surface the most recent one so the user can
  // reopen the invoice with one tap while it awaits payment / delivery.
  const pendingSub = orders.find((o) => o.subtype === 'premium_subscription' && o.status !== 'delivered' && o.status !== 'cancelled');
  const isPremium = !!user.is_premium;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-full jw-gradient-bg grid place-items-center text-white text-xl font-bold">{user.name?.[0]?.toUpperCase()}</div>
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">{user.name}</h1>
          <p className="text-white/60 text-sm">{user.email}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
        <Card className="jw-card p-5 relative overflow-hidden jw-gradient-border">
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full opacity-25 blur-2xl jw-gradient-bg" />
          <Award className="w-6 h-6 text-orange-300" />
          <p className="text-white/60 text-sm mt-3">{t('profile.loyaltyPoints', 'Loyalty Points')}</p>
          <p className="font-display text-3xl font-bold mt-1" data-testid="profile-loyalty-points">{user.loyalty_points || 0}</p>
          <p className="text-white/50 text-xs mt-1">{t('profile.loyaltyEarn', 'Earn 1 point per SAR spent')}</p>
        </Card>
        <Card className="jw-card p-5">
          <ShoppingBag className="w-6 h-6 text-sky-300" />
          <p className="text-white/60 text-sm mt-3">{t('nav.orders', 'Orders')}</p>
          <p className="font-display text-3xl font-bold mt-1">{orders.length}</p>
        </Card>
        <Card className="jw-card p-5">
          <Sparkles className="w-6 h-6 text-pink-300" />
          <p className="text-white/60 text-sm mt-3">{t('profile.savedDesigns', 'Saved Designs')}</p>
          <p className="font-display text-3xl font-bold mt-1">{designs.length}</p>
        </Card>
      </div>

      <Card className="jw-card p-6 mt-6 relative overflow-hidden">
        <div className="absolute -right-8 -top-8 w-52 h-52 rounded-full opacity-20 blur-3xl jw-gradient-bg" />
        <div className="relative flex items-center gap-4 flex-wrap">
          <Crown className="w-8 h-8 text-yellow-300" />
          <div className="min-w-0">
            <h3 className="font-display text-xl font-bold">{t('profile.premium', 'Jwalek Premium')}</h3>
            <p className="text-white/60 text-sm">{t('profile.premiumDesc', 'Exclusive designs, free shipping, priority support.')}</p>
          </div>
          <div className="ml-auto">
            <span
              data-testid="profile-premium-status"
              className={`text-xs px-2 py-1 rounded-full border ${isPremium ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-200' : 'border-white/10 bg-white/[0.05] text-white/80'}`}
            >
              {isPremium ? t('profile.active', 'Active') : t('profile.notActive', 'Not active')}
            </span>
          </div>
        </div>
        {pendingSub && (
          <div
            data-testid="profile-pending-subscription"
            className="relative mt-4 flex items-start gap-3 p-3 rounded-lg border"
            style={{ background: 'rgba(249,115,22,0.08)', borderColor: 'rgba(249,115,22,0.35)' }}
          >
            <Clock className="w-4 h-4 mt-0.5 text-orange-300 shrink-0" />
            <div className="flex-1 text-sm">
              <p className="font-medium">
                {t('subscribe.awaitingTitle', 'Awaiting payment — {tier}').replace('{tier}', pendingSub.premium_tier_name || 'Premium')}
              </p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--jw-text-muted)' }}>
                {t('subscribe.awaitingSub', 'Invoice {no} · once payment clears your perks activate automatically.').replace('{no}', pendingSub.order_number)}
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button
                size="sm"
                variant="secondary"
                data-testid="profile-pending-invoice-button"
                onClick={() => openInvoice(pendingSub.id)}
              >
                {t('subscribe.viewInvoice', 'View invoice')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                data-testid="profile-pending-open-order-button"
                onClick={() => navigate(`/orders/${pendingSub.id}`)}
              >
                {t('subscribe.openOrder', 'Open order')}
              </Button>
            </div>
          </div>
        )}
      </Card>

      {tiers.length > 0 && (
        <div className="mt-6" data-testid="profile-premium-tiers">
          <h3 className="font-display text-lg font-semibold mb-3">{t('profile.premiumTiers', 'Premium plans')}</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            {tiers.map((tier) => {
              const isCurrent = isPremium && user.premium_tier_id === tier.id;
              const isPending = pendingSub && pendingSub.premium_tier_id === tier.id;
              return (
                <Card key={tier.id} className="jw-card p-5 relative overflow-hidden flex flex-col" data-testid={`profile-tier-${tier.id}-card`}>
                  <div className="flex items-baseline justify-between">
                    <h4 className="font-display text-xl font-bold flex items-center gap-2">
                      {tier.name}
                      {isCurrent && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-400/30">
                          {t('subscribe.currentPlan', 'Current plan')}
                        </span>
                      )}
                    </h4>
                    <p className="text-2xl font-bold">{format(tier.price_monthly)}<span className="text-xs" style={{ color: 'var(--jw-text-subtle)' }}> {t('profile.perMonth', '/ mo')}</span></p>
                  </div>
                  <ul className="mt-4 space-y-2 flex-1">
                    {(tier.benefits || []).map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5">
                    <Button
                      className="jw-btn-glossy w-full disabled:opacity-60"
                      disabled={isCurrent || isPending}
                      data-testid={`profile-tier-${tier.id}-subscribe-button`}
                      onClick={() => setSelectedTier(tier)}
                    >
                      {isCurrent
                        ? t('subscribe.alreadyActive', 'Already active')
                        : isPending
                          ? t('subscribe.invoicePending', 'Invoice pending')
                          : t('subscribe.startTier', 'Start {tier}').replace('{tier}', tier.name)}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {rules?.enabled && (
        <Card className="jw-card p-5 mt-6" data-testid="profile-loyalty-rules">
          <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Award className="w-5 h-5 text-orange-300" /> {t('profile.howItWorks', 'How loyalty works')}</h3>
          <div className="grid sm:grid-cols-3 gap-3 mt-3 text-sm" style={{ color: 'var(--jw-text-muted)' }}>
            <div>{t('profile.rulesEarn', 'Earn {pts} pt per {amt} spent.').replace('{pts}', String(rules.points_per_currency)).replace('{amt}', format(1))}</div>
            <div>{t('profile.rulesRedeem', 'Redeem 100 pts for {off} off (min order {min}).').replace('{off}', format(rules.currency_per_point * 100)).replace('{min}', format(rules.min_order_to_redeem))}</div>
            <div>{t('profile.rulesExpiry', 'Points expire after {n} days.').replace('{n}', String(rules.expiry_days))}</div>
          </div>
        </Card>
      )}

      <SubscribeDialog
        tier={selectedTier}
        open={!!selectedTier}
        onClose={() => setSelectedTier(null)}
        onSubscribed={() => { loadOrders(); refreshUser?.(); }}
      />
    </div>
  );
};

export default Profile;
